import * as React from 'react';
import {
    CatalogueCatalogueRead,
    CatalogueJsonldCatalogueCreate,
    CatalogueJsonldCatalogueUpdate,
} from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { FormObject } from '@europrocurement/flexy-form';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import { useOffreService, useSociete } from '@europrocurement/l2d-domain';
import ContentStep from './ContentStep';
import VisibilityStep from './VisibilityStep';
import OverrideStep from './OverrideStep';
import { ModelForm } from '../../forms/types';
import MultiStepForm from '../../components/form/MultiStepForm/MultiStepForm';
import OptionsStep from './OptionsStep';
import { CatalogFormData } from './validation';
import { formValuesToPayload, ressourceToFormValues } from '../../PayloadAdapter/catalog';

/** Catalog Form : Splitted form in 4 steps
 *
 * Steps :
 * 1) Content : Catalog main informations
 * 2) Visibility :  Offer / Origin / Dates
 * 3) Overrides : List of package articles
 * 4) Options : Documents / Additional fields
 *
 * Available form actions : Create / Update
 */
type CatalogFormProps = ModelForm<CatalogueCatalogueRead> & {
    onCancel?: () => void;
};

const Form: React.FunctionComponent<CatalogFormProps> = function (props) {
    const { catalogModel: model } = useOffreService();

    const { entity = {}, isCreate = false, afterSubmit, onCancel, onLabelChange } = props;
    const { request } = useApiRequest();

    const companyId = useSociete();

    const successCallback = () => {
        afterSubmit();
    };

    /**
     * Règles métier (création) : on bloque l'enregistrement et on signale l'onglet
     * concerné tant que les informations obligatoires ne sont pas renseignées.
     * Contrôles ciblés (et non le schéma complet de l'étape) pour ne pas sur-bloquer :
     * un catalogue peut référencer un package OU un groupement.
     * Évite notamment de soumettre sans date de début (l'adaptateur de payload
     * exige une date Luxon valide -> `startDate.setLocale`).
     */
    const validateContent = (values: FieldValues): string | undefined => {
        const hasCommercialName =
            typeof values.commercialName === 'string' && values.commercialName.trim().length > 0;
        const hasDomain = typeof values.domainId === 'number';
        const hasPackageOrGroup =
            typeof values.packageId === 'number' || typeof values.groupId === 'number';

        return hasCommercialName && hasDomain && hasPackageOrGroup
            ? undefined
            : 'Veuillez compléter le contenu du catalogue (domaine, nom commercial, package ou groupement)';
    };

    const validateVisibility = (values: FieldValues): string | undefined => {
        const hasOffer = typeof values.offerId === 'number';
        // Date de début obligatoire : objet Luxon (possède un `ts` numérique).
        const startDate = values.startDate as { ts?: unknown } | null | undefined;
        const hasStartDate = !!startDate && typeof startDate.ts === 'number';

        return hasOffer && hasStartDate
            ? undefined
            : "Veuillez renseigner l'offre et la date de début (onglet Visibilité)";
    };

    /** Form steps definitions */

    const formSteps = [
        {
            title: 'Contenu du catalogue',
            component: ContentStep,
            validate: isCreate ? validateContent : undefined,
        },
        {
            title: 'Visibilité',
            component: VisibilityStep,
            validate: isCreate ? validateVisibility : undefined,
        },
        {
            title: 'Surcharges',
            component: OverrideStep,
        },
        {
            title: 'Éléments optionnels',
            component: OptionsStep,
        },
    ];

    /** Form action | Creation & Update */

    /** Form input formatting */
    const getDefaultValues = () => ({
        companyId,
        formOptions: { packages: [], groups: [], offers: [], origins: [] },
        readOnlyDocuments: {
            articles: [],
            options: [],
            formalities: [],
        }, // ReadOnly related documents (Articles documents / Formalities ...)
    });

    const initiaValues = isCreate ? getDefaultValues() : ressourceToFormValues(entity);

    /** Form output formatting */
    const createModel: SubmitHandler<FormObject> = async (formValues) => {
        const payload = formValuesToPayload(formValues as CatalogFormData);
        const createRequest = model.create({
            catalogueJsonldCatalogueCreate: payload as unknown as CatalogueJsonldCatalogueCreate,
            xIdSociete: companyId,
        });
        await request(createRequest, {
            successCallback: (result) => afterSubmit(result.data?.id),
        });
    };

    const updateModel: SubmitHandler<FormObject> = async (formValues) => {
        if (entity.id) {
            const payload = formValuesToPayload(formValues as CatalogFormData);
            const updateRequest = model.update({
                id: entity.id.toString(),
                catalogueJsonldCatalogueUpdate:
                    payload as unknown as CatalogueJsonldCatalogueUpdate,
                xIdSociete: companyId,
            });
            await request(updateRequest, { successCallback });
        }
    };

    const formAction = isCreate ? createModel : updateModel;

    return (
        <MultiStepForm
            steps={formSteps}
            initiaValues={initiaValues}
            submitAction={formAction}
            afterSubmit={afterSubmit}
            isCreate={isCreate}
            onCancel={onCancel}
            onLabelChange={onLabelChange}
        />
    );
};

export default Form;
